import { Button } from "@/components/ui/button";
import { Heart, BookOpen, Coins, Users } from "lucide-react";

const CinematicHeroEn = () => {
  return (
    <section className="relative min-h-screen w-full overflow-hidden bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Decorative elements */}
      <div className="absolute top-20 right-[10%] w-96 h-96 bg-gradient-to-br from-purple-400/20 to-pink-400/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-[5%] w-64 h-64 bg-gradient-to-br from-orange-400/20 to-yellow-400/20 rounded-full blur-3xl"></div>

      <div className="container relative h-screen flex items-center">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center px-4">
          {/* Left Column - Main Message */}
          <div className="space-y-8 animate-fade-in">
            <div className="space-y-4">
              <div className="inline-block bg-legatia-green/10 rounded-full px-6 py-2">
                <span className="text-sm font-semibold text-legatia-green">Turn moments into memories</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                Every moment becomes a{" "}
                <span className="text-legatia-green">memory</span> and{" "}
                <span className="text-legatia-green">capital</span>
              </h1>
              
              <p className="text-xl text-muted-foreground max-w-2xl">
                Turn childhood gifts into memories and savings. Legatia combines an emotional keepsake with smart investments for their future.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="bg-legatia-green hover:bg-legatia-green/90 text-white shadow-lg hover:shadow-xl transition-all hover:scale-105"
                onClick={() => window.location.href = "https://calendly.com/legatia/15min"}
              >
                <Heart className="mr-2 h-5 w-5" />
                Start today
              </Button>
              
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-legatia-green text-legatia-green hover:bg-legatia-green/10"
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <BookOpen className="mr-2 h-5 w-5" />
                See how it works
              </Button>
            </div>

            {/* Social Proof */}
            <div className="flex items-center gap-6 pt-4">
              <div className="flex -space-x-3">
                {[1, 2, 3, 4].map((i) => (
                  <div 
                    key={i} 
                    className="w-10 h-10 rounded-full border-2 border-white bg-gradient-to-br from-legatia-green/30 to-babyBlue-light flex items-center justify-center"
                  >
                    <Users className="h-5 w-5 text-legatia-green" />
                  </div>
                ))}
              </div>
              <div>
                <p className="text-sm font-semibold">Join 500+ families</p>
                <p className="text-xs text-muted-foreground">who are building their children's future</p>
              </div>
            </div>
          </div>

          {/* Right Column - Visual Impact */}
          <div className="relative lg:block hidden">
            <div className="relative">
              {/* Center card - Main Moment */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                <div className="bg-white rounded-2xl shadow-2xl p-6 w-80 transform hover:scale-105 transition-all duration-300 border-2 border-purple-100">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
                      <Heart className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">First Birthday</h3>
                      <p className="text-sm text-muted-foreground">May 15, 2024</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Gifts collected</span>
                      <span className="font-bold text-legatia-green">€850</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Contributors</span>
                      <span className="font-bold">12 loved ones</span>
                    </div>
                    <div className="w-full bg-gradient-to-r from-purple-100 to-pink-100 rounded-full h-2 overflow-hidden">
                      <div className="bg-gradient-to-r from-purple-500 to-pink-500 h-full w-3/4 rounded-full animate-pulse-slow"></div>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Coins className="h-4 w-4 text-legatia-green" />
                        <span className="text-sm font-medium">Invested at 18</span>
                      </div>
                      <span className="font-bold text-lg text-legatia-green">~€2,347</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating cards */}
              <div className="absolute top-0 left-0 animate-float">
                <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-4 w-48">
                  <div className="flex items-center gap-2">
                    <Heart className="h-5 w-5 text-pink-500" />
                    <span className="text-sm font-medium">First word</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Photo + €150</p>
                </div>
              </div>

              <div className="absolute bottom-0 right-0 animate-float" style={{ animationDelay: '1s' }}>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-4 w-48">
                  <div className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-blue-500" />
                    <span className="text-sm font-medium">First day of school</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Video + €300</p>
                </div>
              </div>

              <div className="absolute top-1/4 right-0 animate-float" style={{ animationDelay: '0.5s' }}>
                <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-4 w-48">
                  <div className="flex items-center gap-2">
                    <Coins className="h-5 w-5 text-legatia-green" />
                    <span className="text-sm font-medium">Christmas 2024</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Album + €520</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default CinematicHeroEn;
